import React, { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { Button, List, Modal, Tag, Typography, message } from 'antd'
import type { ButtonProps } from 'antd'

import {
  LEGAL_FORMALITY_MOVABLE_CONTACTS_QUERY,
  SWITCH_CONTACT_FORMALITY_MUTATION,
} from 'graphql/LegalFormalities'

interface MovableContact {
  id: string
  society?: {
    id: string
    name?: string
    legalStatus?: { id: number; name?: string }
  }
  domiciliation?: {
    id: number
    activated: boolean
    subscriptionState?: string
    clientSince?: string
    createdAt: string
    lengthCommitment?: number
  }
}

interface IProps {
  legalFormalityId: number | string
  size?: ButtonProps['size']
}

interface MovableContactItem {
  blockedReason?: string | null
  contact: MovableContact
}

const formatDate = (date?: string) =>
  date ? dayjs(date).format('DD/MM/YYYY') : '-'

const domiciliationTag = (
  dom: NonNullable<MovableContact['domiciliation']>
) => {
  if (dom.subscriptionState === 'in_progress') {
    return <Tag color="blue">En cours</Tag>
  }

  return dom.activated ? (
    <Tag color="green">Active</Tag>
  ) : (
    <Tag color="red">Résiliée</Tag>
  )
}

const FormalityMoveBtn = ({ legalFormalityId, size }: IProps) => {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [movingContactId, setMovingContactId] = useState<string>()

  const { data } = useQuery(LEGAL_FORMALITY_MOVABLE_CONTACTS_QUERY, {
    variables: { id: legalFormalityId },
    fetchPolicy: 'no-cache',
  })
  const [switchContact] = useMutation(SWITCH_CONTACT_FORMALITY_MUTATION, {
    refetchQueries: ['getLegalFormalities'],
  })

  const items: MovableContactItem[] =
    data?.legalFormality?.movableContacts || []

  if (items.length === 0) {
    return <></>
  }

  const handleSelect = (contactId: string) => {
    setMovingContactId(contactId)
    switchContact({
      variables: { id: Number(legalFormalityId), contactId },
    })
      .then(({ data }) => {
        const errors = data?.legalFormalitySwitchContact?.errors || []
        if (errors.length > 0) {
          message.error(errors.join(', '))
          return
        }

        message.success('Formalité déplacée')
        setOpen(false)
        navigate(`/contacts/${contactId}/legal_formalities/${legalFormalityId}`)
      })
      .catch(e => {
        console.error(e)
        message.error('Erreur lors du déplacement de la formalité')
      })
      .finally(() => setMovingContactId(undefined))
  }

  const renderDomiciliation = (contact: MovableContact) => {
    const dom = contact.domiciliation
    if (!dom) {
      return <span>Pas de domiciliation</span>
    }

    return (
      <span>
        {domiciliationTag(dom)}
        {`Commandée le ${formatDate(dom.clientSince || dom.createdAt)}`}
        {' - '}
        {`Engagement : ${
          dom.lengthCommitment != null ? `${dom.lengthCommitment} mois` : '-'
        }`}
      </span>
    )
  }

  return (
    <>
      <Button size={size} onClick={() => setOpen(true)}>
        Déplacer
      </Button>
      <Modal
        title="Déplacer la formalité vers un autre contact"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={800}
      >
        <List
          dataSource={items}
          renderItem={({ contact, blockedReason }) => (
            <List.Item
              actions={[
                <Button
                  key="select"
                  type="primary"
                  loading={movingContactId === contact.id}
                  disabled={!!movingContactId || !!blockedReason}
                  onClick={() => handleSelect(contact.id)}
                >
                  Sélectionner
                </Button>,
              ]}
            >
              <List.Item.Meta
                title={`[${contact.id}] ${contact.society?.name || '-'} (${
                  contact.society?.legalStatus?.name || '-'
                })`}
                description={
                  <>
                    {renderDomiciliation(contact)}
                    {blockedReason && (
                      <div>
                        <Typography.Text type="danger">
                          {blockedReason}
                        </Typography.Text>
                      </div>
                    )}
                  </>
                }
              />
            </List.Item>
          )}
        />
      </Modal>
    </>
  )
}

export default FormalityMoveBtn
